import React, { useState } from "react";

import { ArrowLeftIcon, ArrowRightIcon } from "@/assets/icons";
import ToolTipBase from "@/components/ui/tooltip";
import { resolveVisibleValueLabels } from "@/helpers/chartHelper";
import { formatMeasure } from "@/utils/format";
import { compactNumber } from "@/utils/number";

import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { twMerge } from "tailwind-merge";

import ChartLegend from "./ChartLegend";
import { ChartTooltipContent } from "./GroupedChartTooltip";
import {
  buildSeriesFromData,
  ChartSeries,
  domainTickCalculate,
  resolveCategoryKey,
  shouldRotateAxisLabels
} from "./utils";

const MAX_VISIBLE_BARS = 20;
const MAX_LABEL_LENGTH = 8;
// Width reserved for the pinned Y-axis strip beside the scroll area.
const Y_AXIS_STRIP_WIDTH = 60;
// Vertical gap between a value label and the top of its bar.
const VALUE_LABEL_OFFSET_Y = 8;

interface ValueLabelProps {
  x?: string | number;
  y?: string | number;
  width?: string | number;
  value?: string | number;
  index?: number;
  /** Indexes allowed to render; undefined while the chart width is unknown. */
  visibleIndexes?: Set<number>;
}

// Recharts injects x/y/width/value/index; `visibleIndexes` comes from the
// element we hand to LabelList and survives its cloning.
const ValueLabel = ({
  x,
  y,
  width,
  value,
  index,
  visibleIndexes
}: ValueLabelProps) => {
  if (visibleIndexes && !visibleIndexes.has(Number(index))) return null;

  return (
    <text
      x={Number(x) + Number(width) / 2}
      y={Number(y) - VALUE_LABEL_OFFSET_Y}
      textAnchor="middle"
      fill="#000000B2"
      fontWeight={700}
      fontSize={12}
    >
      {formatMeasure(Number(value), "chart")}
    </text>
  );
};

interface XAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string | number };
  /** When false, labels are drawn straight and un-truncated. Defaults to diagonal. */
  isDiagonal?: boolean;
}

const XAxisTick = ({
  x = 0,
  y = 0,
  payload,
  isDiagonal = true
}: XAxisTickProps) => {
  const fullLabel = String(payload?.value ?? "");

  if (!isDiagonal) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#000000B2"
          fontSize={12}
          className="cursor-default"
        >
          {fullLabel}
        </text>
      </g>
    );
  }

  const isTruncated = fullLabel.length > MAX_LABEL_LENGTH;
  const displayLabel = isTruncated
    ? `${fullLabel.slice(0, MAX_LABEL_LENGTH)}...`
    : fullLabel;

  return (
    <g transform={`translate(${x},${y})`}>
      {isTruncated ? (
        <ToolTipBase content={fullLabel} position="top-center">
          <text
            x={-15}
            y={10}
            dy={10}
            textAnchor="middle"
            fill="#000000B2"
            transform="rotate(-45)"
            fontSize={12}
            className="cursor-pointer"
          >
            {displayLabel}
          </text>
        </ToolTipBase>
      ) : (
        <text
          x={-15}
          y={10}
          dy={10}
          textAnchor="middle"
          fill="#000000B2"
          transform="rotate(-45)"
          fontSize={12}
          className="cursor-default"
        >
          {displayLabel}
        </text>
      )}
    </g>
  );
};

export interface VerticalBarChartProps {
  data: Record<string, string | number>[];
  /** Key in each data item to use as the X-axis category label. Auto-detected if omitted. */
  categoryKey?: string;
  /** One entry per bar segment. Auto-built from data keys if omitted. */
  series?: ChartSeries[];
  height?: number;
  title?: string;
  onBarClick?: (name: string) => void;
  isResponsive?: boolean;
  /** Aggregation appended to the legend label when the chart is single-series. */
  aggregation?: string;
  /**
   * Caps bar thickness. "fill" leaves it uncapped so Recharts splits the
   * category band between the series. Defaults to 10 when grouped, 30 otherwise.
   */
  maxBarSize?: number | "fill";
  /**
   * Rounding on the top corners of every bar. Defaults to 10 when grouped and
   * 999 — a fully round cap — otherwise, so a chart's corners otherwise change
   * with its series count.
   */
  cornerRadius?: number;
  /**
   * Lays the chart out for paper: every bar on one page, fitted to the
   * container width, and no animation so a print captures the finished chart.
   */
  isPrint?: boolean;
}

interface VerticalBarChartItemProps {
  width?: number;
  height?: number;
  chartData: Record<string, string | number>[];
  categoryKey: string;
  series: ChartSeries[];
  axisDomainMax: number;
  axisTicks: number[];
  totalValue: number;
  isGrouped: boolean;
  onBarClick?: (name: string) => void;
  title?: string;
  /** Hide the Y-axis — used when it is pinned in a separate strip alongside. */
  hideYAxis?: boolean;
  maxBarSize?: number | "fill";
  cornerRadius?: number;
  isPrint?: boolean;
}

const VerticalBarChartItem = ({
  width,
  height,
  chartData,
  categoryKey,
  series,
  axisDomainMax,
  axisTicks,
  totalValue,
  isGrouped,
  onBarClick,
  title,
  hideYAxis,
  maxBarSize,
  cornerRadius,
  isPrint
}: VerticalBarChartItemProps) => {
  // undefined is Recharts' "no cap": bars then take the whole category band
  // (split between series) and grow with the container.
  const barSize =
    maxBarSize === "fill" ? undefined : (maxBarSize ?? (isGrouped ? 10 : 30));

  // `width` (injected by ResponsiveContainer) minus the Y-axis strip and right
  // margin gives the plot band. Default to diagonal until it's known (avoids a flash).
  const plotWidth = (width ?? 0) - (hideYAxis ? 0 : Y_AXIS_STRIP_WIDTH) - 20;
  const isDiagonalLabels =
    !width ||
    shouldRotateAxisLabels(
      chartData.map((item) => String(item[categoryKey] ?? "")),
      plotWidth
    );
  // Value labels are shown by default; only the ones that would collide with
  // the previous visible label drop out. Left undefined until the container
  // reports its width so labels don't flash in and out.
  const visibleValueLabels = React.useMemo(() => {
    if (isGrouped || !width) return undefined;
    return resolveVisibleValueLabels(
      chartData.map((item) =>
        formatMeasure(Number(item[series[0].dataKey]), "chart")
      ),
      plotWidth / chartData.length
    );
  }, [isGrouped, width, chartData, series, plotWidth]);

  // Tighten the spacing between bars within a category only for the kit's own
  // 10px grouped bars (no maxBarSize given) with few series (< 5); otherwise
  // keep Recharts' default gap of 4. Once a caller sets a width — a cap or
  // "fill" — the bars are wide enough that a negative gap would only park the
  // right bar on top of its neighbour.
  const barGap =
    maxBarSize === undefined && isGrouped && series.length < 5 ? -20 : 4;
  const topRadius = cornerRadius ?? (isGrouped ? 10 : 999);
  const barRadius: [number, number, number, number] = [
    topRadius,
    topRadius,
    0,
    0
  ];
  return (
    <BarChart
      width={width}
      height={height}
      data={chartData}
      margin={{ top: 20, right: 20, left: 0, bottom: -15 }}
      barGap={barGap}
      aria-label={title}
    >
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis
        dataKey={categoryKey}
        type="category"
        height={80}
        tickLine={false}
        interval={0}
        tick={<XAxisTick isDiagonal={isDiagonalLabels} />}
      />
      <YAxis
        type="number"
        domain={[0, axisDomainMax]}
        ticks={axisTicks}
        allowDecimals={false}
        tickLine={false}
        axisLine={false}
        tickFormatter={(v) => compactNumber(v)}
        tick={{ fill: "#666", fontSize: 12, fontWeight: 500 }}
        hide={hideYAxis}
      />
      <Tooltip
        content={
          <ChartTooltipContent isGrouped={isGrouped} totalValue={totalValue} />
        }
      />
      {series.map((s) => (
        <Bar
          key={s.dataKey}
          dataKey={s.dataKey}
          name={s.label ?? s.dataKey}
          fill={s.color}
          maxBarSize={barSize}
          radius={barRadius}
          isAnimationActive={!isPrint}
          onClick={(data) => onBarClick?.(String(data[categoryKey]))}
        >
          {!isGrouped && (
            <LabelList
              dataKey={s.dataKey}
              position="top"
              content={<ValueLabel visibleIndexes={visibleValueLabels} />}
            />
          )}
        </Bar>
      ))}
    </BarChart>
  );
};

const VerticalBarChart = React.forwardRef<
  HTMLDivElement,
  VerticalBarChartProps
>((props, ref) => {
  const [currentPage, setCurrentPage] = useState(0);
  const {
    data,
    categoryKey: categoryKeyProp,
    series: seriesProp,
    height = 400,
    title,
    onBarClick,
    isResponsive = true,
    aggregation,
    maxBarSize,
    cornerRadius,
    isPrint = false
  } = props;

  const categoryKey = React.useMemo(
    () => resolveCategoryKey(data, categoryKeyProp),
    [categoryKeyProp, data]
  );

  const series = React.useMemo(
    () => seriesProp ?? buildSeriesFromData(data, categoryKey),
    [seriesProp, data, categoryKey]
  );

  const isGrouped = series.length > 1;

  const totalPages = Math.ceil(data.length / MAX_VISIBLE_BARS);
  const startIndex = currentPage * MAX_VISIBLE_BARS;
  const showNavigation = !isPrint && data.length > MAX_VISIBLE_BARS;
  const visibleData = showNavigation
    ? data.slice(startIndex, startIndex + MAX_VISIBLE_BARS)
    : data;

  React.useEffect(() => {
    if (isResponsive) setCurrentPage(0);
  }, [data.length, isResponsive]);

  const BAR_WIDTH = isGrouped ? Math.max(60, series.length * 12) : 40;
  const chartWidth = Math.max(data.length * BAR_WIDTH, 424);

  const numericValues = React.useMemo(
    () =>
      data
        .flatMap((item) => series.map((s) => Number(item[s.dataKey])))
        .filter((v) => !Number.isNaN(v)),
    [data, series]
  );

  const maxValue = numericValues.length > 0 ? Math.max(...numericValues, 0) : 0;

  const { axisDomainMax, axisTicks } = React.useMemo(
    () => domainTickCalculate(numericValues, maxValue),
    [numericValues, maxValue]
  );

  const totalValue = numericValues.reduce((sum, v) => sum + v, 0);

  const handlePrevious = () => setCurrentPage((prev) => Math.max(0, prev - 1));

  const handleNext = () =>
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));

  if (!data.length || totalValue === 0)
    return (
      <div className="flex-center h-[400px] px-4">
        <p className="text-base text-txtHeading font-semibold text-center">
          No data available to display. Please check your data or try adjusting
          the configuration menu.
        </p>
      </div>
    );

  return (
    <div ref={ref} className="w-full relative flex flex-col">
      {isResponsive || isPrint ? (
        <div className="flex items-center">
          {showNavigation && (
            <button
              onClick={handlePrevious}
              disabled={currentPage === 0}
              aria-label="Previous page"
              className={twMerge(
                "flex items-center justify-center w-8 h-8 rounded-full transition-colors flex-shrink-0 mr-2",
                currentPage === 0
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              )}
            >
              <ArrowLeftIcon className="w-8 h-8" />
            </button>
          )}
          <ResponsiveContainer width="100%" height={height}>
            <VerticalBarChartItem
              chartData={visibleData}
              categoryKey={categoryKey}
              series={series}
              axisDomainMax={axisDomainMax}
              axisTicks={axisTicks}
              totalValue={totalValue}
              isGrouped={isGrouped}
              maxBarSize={maxBarSize}
              cornerRadius={cornerRadius}
              onBarClick={onBarClick}
              title={title}
              isPrint={isPrint}
            />
          </ResponsiveContainer>
          {showNavigation && (
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages - 1}
              aria-label="Next page"
              className={twMerge(
                "flex items-center justify-center w-8 h-8 rounded-full transition-colors flex-shrink-0 ml-2 fill-dim-gray",
                currentPage === totalPages - 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
              )}
            >
              <ArrowRightIcon className="w-8 h-8" />
            </button>
          )}
        </div>
      ) : (
        <div className="flex">
          {/* Pinned Y-axis — an empty chart that renders only the numeric axis,
              with the X-axis height reserved so its ticks align with the bars. */}
          <div className="shrink-0" style={{ width: Y_AXIS_STRIP_WIDTH }}>
            <BarChart
              width={Y_AXIS_STRIP_WIDTH}
              height={height}
              data={data}
              margin={{ top: 20, right: 0, left: 0, bottom: -15 }}
            >
              <YAxis
                type="number"
                domain={[0, axisDomainMax]}
                ticks={axisTicks}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                width={Y_AXIS_STRIP_WIDTH}
                tickFormatter={(v) => compactNumber(v)}
                tick={{ fill: "#666", fontSize: 12, fontWeight: 500 }}
              />
              <XAxis
                dataKey={categoryKey}
                type="category"
                height={80}
                tick={false}
                tickLine={false}
                axisLine={false}
              />
            </BarChart>
          </div>
          {/* Scrolling bar area — the Y-axis is hidden here and pinned left.
              min-width keeps the chart's natural width (scrolls when narrower);
              w-full lets it fill the parent when there's room. */}
          <div className="flex-1 overflow-x-auto">
            <div className="w-full" style={{ minWidth: chartWidth }}>
              <ResponsiveContainer width="100%" height={height}>
                <VerticalBarChartItem
                  chartData={data}
                  categoryKey={categoryKey}
                  series={series}
                  axisDomainMax={axisDomainMax}
                  axisTicks={axisTicks}
                  totalValue={totalValue}
                  isGrouped={isGrouped}
                  maxBarSize={maxBarSize}
                  cornerRadius={cornerRadius}
                  onBarClick={onBarClick}
                  title={title}
                  hideYAxis
                />
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
      <ChartLegend series={series} iconType="rect" aggregation={aggregation} />
    </div>
  );
});

VerticalBarChart.displayName = "VerticalBarChart";

export default VerticalBarChart;
